import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { NASProductFareFragment, NASProductFragment, NASStatus, PassengerFragment } from '@websky/graphql';
import { Money } from '../../../../../types';
import { LoungeSelectedService } from '../../types';
import { PassengerType } from '../../../../../enums';
import { useTheme } from '../../../../../theme';
import EveryPortCard from '../../../../../EveryPortCard/EveryPortCard/EveryPortCard';
import EveryPortTraveller from './EveryPortTraveller/EveryPortTraveller';

interface SelectedServicePassengerOption {
	traveller: PassengerFragment;
	fare: NASProductFareFragment;
	price: Money;
	active?: boolean;
	confirmed?: boolean;
	disabled?: boolean;
}

interface Props {
	service: NASProductFragment;
	onCancel?: () => void;
	travellers: PassengerFragment[];
	addTraveller?: (travellerId: string, product: NASProductFragment, fare: NASProductFareFragment) => void;
	removeTraveller?: (travellerId: string, product: NASProductFragment, fare: NASProductFareFragment) => void;
	selectedTravellersInSegment: LoungeSelectedService[];
	modifyAvailable?: boolean;
}

const SelectedService: React.FC<Props> = ({
	service,
	onCancel,
	travellers,
	selectedTravellersInSegment,
	removeTraveller,
	addTraveller,
	modifyAvailable
}) => {
	const { t } = useTranslation('ServiceAggregation');
	const { EveryPortLounge: theme } = useTheme('CheckoutAdditionalService');

	const travellersMap = React.useMemo(() => {
		return new Map(travellers.map(traveller => [traveller.id, traveller]));
	}, [travellers]);

	const selectedTravellersSet = React.useMemo(() => {
		const selectedTravellersByServiceId = selectedTravellersInSegment.filter(
			item => item.product.id === service.id
		);

		return new Set(selectedTravellersByServiceId.map(item => item.travellerId));
	}, [selectedTravellersInSegment, service.id]);

	const travellerOptions = React.useMemo(() => {
		const travellersArray: SelectedServicePassengerOption[] = [];

		for (const fare of service.fares) {
			for (const priceItem of fare.prices) {
				const travellerId = priceItem.traveller.id;
				const traveller = travellersMap.get(travellerId);

				if (!traveller) {
					continue;
				}

				const travellerFromState = selectedTravellersInSegment.find(
					item => item.travellerId === travellerId && item.product.id === service.id
				);

				const active = selectedTravellersSet.has(travellerId);
				const confirmed = active && travellerFromState?.status !== NASStatus.New;

				travellersArray.push({
					traveller,
					fare,
					active,
					confirmed,
					price: priceItem.price,
					disabled: !active && !modifyAvailable
				});
			}
		}

		return travellersArray.sort((a, b) => Number(a.confirmed) - Number(b.confirmed));
	}, [service.fares, service.id, selectedTravellersInSegment, modifyAvailable, travellersMap, selectedTravellersSet]);

	const noAdultsInSelectedTravellers = React.useMemo(() => {
		const activeItems = travellerOptions.filter(t => t.active);

		return activeItems.length > 0 && activeItems.every(t => t.traveller.type !== PassengerType.Adult);
	}, [travellerOptions]);

	const allAdultsPassengerSelected = React.useMemo(() => {
		return travellerOptions.every(t => t.traveller.type !== PassengerType.Adult || t.active);
	}, [travellerOptions]);

	/**
	 * Отслеживание автоматически добавленных детей
	 *
	 * Жизненный цикл:
	 * - Инициализация: пустой Set при первом рендере
	 * - Добавление: записываем ID сразу после вызова addTraveller()
	 * - Удаление: очищаем ID при ручном удалении путешественника (handleTravellerClick)
	 * - Cleanup: полная очистка при размонтировании компонента
	 */
	const autoAddedChildrenRef = React.useRef(new Set<string>());

	React.useEffect(() => {
		if (!allAdultsPassengerSelected || !addTraveller) {
			return;
		}

		const childrenToAdd = travellerOptions.filter(
			t =>
				t.traveller.type !== PassengerType.Adult &&
				!t.active &&
				!autoAddedChildrenRef.current.has(t.traveller.id)
		);

		if (!childrenToAdd.length) {
			return;
		}

		childrenToAdd.forEach(child => {
			addTraveller(child.traveller.id, service, child.fare);
			autoAddedChildrenRef.current.add(child.traveller.id);
		});
	}, [allAdultsPassengerSelected, travellerOptions, service, addTraveller]);

	React.useEffect(() => {
		return () => {
			autoAddedChildrenRef.current.clear();
		};
	}, []);

	const handleTravellerClick = React.useCallback(
		(traveller: SelectedServicePassengerOption) => {
			if (!removeTraveller || !addTraveller) {
				return;
			}

			if (traveller.active) {
				removeTraveller(traveller.traveller.id, service, traveller.fare);
				autoAddedChildrenRef.current.delete(traveller.traveller.id);
			} else {
				addTraveller(traveller.traveller.id, service, traveller.fare);
			}
		},
		[removeTraveller, addTraveller, service]
	);

	const visibleTravellers = React.useMemo(() => {
		return travellerOptions.filter(t => !t.disabled);
	}, [travellerOptions]);

	const cardSelectHandler = React.useMemo(() => {
		return modifyAvailable ? onCancel : null;
	}, [modifyAvailable, onCancel]);

	return (
		<>
			<EveryPortCard
				key={service.id}
				options={service.facilities}
				terminalName={service.terminalName}
				price={service.price}
				images={service.images}
				name={service.name}
				description={service.description}
				location={service.meetingPoint}
				isSelected={true}
				onSelect={cardSelectHandler}
				category={service.category}
			/>

			<div className={theme.travellers}>
				{visibleTravellers.map((traveller, index) => {
					const isFirstTraveller = index === 0;
					const showTooltip = noAdultsInSelectedTravellers && isFirstTraveller;
					const isChildDisabled =
						traveller.traveller.type !== PassengerType.Adult && allAdultsPassengerSelected;

					return (
						<EveryPortTraveller
							tooltipText={showTooltip ? t('Please choose at least one adult traveller') : null}
							isSelected={traveller.active}
							key={traveller.traveller.id}
							traveller={traveller.traveller}
							price={traveller.price}
							isConfirmed={traveller.confirmed}
							disabled={isChildDisabled}
							onClick={() => handleTravellerClick(traveller)}
						/>
					);
				})}
			</div>
		</>
	);
};

export default React.memo(SelectedService);
